import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import MissionAcceptanceReview, { AcceptanceReviewContent, AcceptanceReviewPresentation } from '../components/common/mission/MissionAcceptanceReview';
import { scenarioReviewChanged } from '../helpers/scenarioReview';
import { useQualityWarning } from './useQualityWarning';

interface PendingReview extends AcceptanceReviewContent {
  warningSessionKey?: string;
  refresh(): Promise<AcceptanceReviewContent>;
  accept(): void;
  attempt: number;
  scope: string;
}

const isForeground = () => (!AppState.currentState || AppState.currentState === 'active') &&
  !(Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden');

export default function useMissionAcceptanceReview(scope: string, onNavigate?: () => void, presentation?: AcceptanceReviewPresentation) {
  const quality = useQualityWarning();
  const qualityRef = useRef(quality);
  qualityRef.current = quality;
  const releaseReview = useRef<(() => void) | undefined>(undefined);
  const [pending, setPending] = useState<PendingReview>();
  const [checking, setChecking] = useState(false);
  const current = useRef<PendingReview | undefined>(undefined);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const focused = useRef(false);
  const sequence = useRef(0);
  const close = useCallback(() => {
    current.current = undefined; setPending(undefined);
    releaseReview.current?.(); releaseReview.current = undefined;
  }, []);
  useEffect(() => { close(); return () => { current.current = undefined; releaseReview.current?.(); releaseReview.current = undefined; }; }, [scope, quality.sessionKey, close]);
  useEffect(() => {
    const review = current.current;
    if (!review || checking || review.warningSessionKey !== quality.sessionKey) return;
    const warning = quality.warning;
    if (review.qualityWarning?.id === warning?.id &&
      review.qualityWarning?.acknowledgedAtUtc === warning?.acknowledgedAtUtc) return;
    const needsManual = !!warning && !warning.acknowledgedAtUtc;
    const next = { ...review, qualityWarning: warning,
      manualConfirmation: review.manualConfirmation || needsManual,
      attempt: needsManual && !review.manualConfirmation ? ++sequence.current : review.attempt };
    current.current = next; setPending(next);
  }, [quality.warning, quality.sessionKey, checking]);
  useFocusEffect(useCallback(() => {
    focused.current = true;
    return () => { focused.current = false; close(); };
  }, [close]));
  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => { if (state !== 'active') close(); });
    const visibility = () => { if (document.visibilityState === 'hidden') close(); };
    if (Platform.OS === 'web' && typeof document !== 'undefined') document.addEventListener('visibilitychange', visibility);
    return () => {
      subscription.remove();
      if (Platform.OS === 'web' && typeof document !== 'undefined') document.removeEventListener('visibilitychange', visibility);
    };
  }, [close]);
  const request = useCallback((content: AcceptanceReviewContent, refresh: PendingReview['refresh'], accept: () => void) => {
    if (scopeRef.current !== scope || !focused.current || !isForeground()) return;
    releaseReview.current ??= qualityRef.current.beginReview();
    const warning = qualityRef.current.warning;
    const next = { ...content, qualityWarning: warning, manualConfirmation: !!warning && !warning.acknowledgedAtUtc,
      warningSessionKey: qualityRef.current.sessionKey, refresh, accept, attempt: ++sequence.current, scope };
    current.current = next; setPending(next);
  }, [scope]);
  const confirm = async () => {
    const review = current.current;
    if (!review || !focused.current || !isForeground()) return;
    setChecking(true);
    try {
      const [latest, warning] = await Promise.all([review.refresh(), qualityRef.current.refresh()]);
      if (current.current !== review || scopeRef.current !== review.scope || qualityRef.current.sessionKey !== review.warningSessionKey || !focused.current || !isForeground()) return;
      if (scenarioReviewChanged(review.notice, latest.notice) ||
        (warning && !warning.acknowledgedAtUtc && warning.id !== review.qualityWarning?.id)) {
        const next = { ...review, ...latest, qualityWarning: warning,
          manualConfirmation: review.manualConfirmation || !!warning && !warning.acknowledgedAtUtc, attempt: ++sequence.current };
        current.current = next; setPending(next);
        return;
      }
      if (warning?.id && !warning.acknowledgedAtUtc) {
        const acknowledged = await qualityRef.current.acknowledge(warning.id);
        if (current.current !== review || scopeRef.current !== review.scope || qualityRef.current.sessionKey !== review.warningSessionKey || !focused.current || !isForeground()) return;
        if (acknowledged && acknowledged.id !== warning.id && !acknowledged.acknowledgedAtUtc) {
          const next = { ...review, ...latest, qualityWarning: acknowledged, manualConfirmation: true, attempt: ++sequence.current };
          current.current = next; setPending(next); return;
        }
      }
      close(); review.accept();
      } finally { setChecking(false); }
  };
  const visible = pending?.scope === scope ? pending : undefined;
  return { request, close, isOpen: !!visible,
    element: visible ? <MissionAcceptanceReview key={visible.attempt} {...visible} {...presentation} onClose={close} onConfirm={confirm} onNavigate={onNavigate} /> : null };
}
